import React, { useContext, createContext, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import { setUserId } from "@crm/analytics";
import { useTranslation } from "react-i18next";
import {
    isFeatureEnabled,
    getNumberOfUsers,
    getCustomPlanNumber,
    isSupportPlan,
} from "@utils";
import AuthModal from "../Chat/components/AuthModal";

const authContext = createContext();

function useProvideAuth({ toggleDarkMode }) {
    const [user, setUser] = React.useState(null);
    const { i18n } = useTranslation();
    const [isLoadingUser, setIsLoadingUser] = React.useState(true);
    const [searchParams] = useSearchParams();
    const isAdmin =
        user?.roles?.some((role) => role?.role?.name === "admin") ||
        user?.roles?.includes("admin");
    const isSuperAdmin =
        user?.roles?.some((role) => role?.role?.name === "super-admin") ||
        user?.roles?.includes("super-admin");
    const isSupport =
        user?.roles?.some((role) => role?.role?.name === "support") ||
        user?.roles?.includes("support");
    const isClient =
        user?.roles?.some((role) => role?.role?.name === "client") ||
        user?.roles?.includes("client");
    const isComplianceOfficer =
        user?.roles?.some(
            (role) => role?.role?.name === "compliance-officer"
        ) || user?.roles?.includes("compliance-officer");
    const canModifyObfuscation =
        isComplianceOfficer ||
        isAdmin ||
        isSuperAdmin ||
        user?.companies?.[0]?.canModifyObfuscation !== false;
    const [showAuthModal, setShowAuthModal] = useState(() => {
        return sessionStorage.getItem("auth_modal_open") === "true";
    });
    const handleSetShowAuthModal = React.useCallback((value) => {
        setShowAuthModal(value);
        if (value) {
            sessionStorage.setItem("auth_modal_open", "true");
        } else {
            sessionStorage.removeItem("auth_modal_open");
            sessionStorage.removeItem("auth_modal_otp_state");
        }
    }, []);
    const didLogin = React.useRef(false);
    const didGetCompanyAgentUICredit = React.useRef(false);
    const [companyAgentUICredit, setCompanyAgentUICredit] = React.useState(-1);

    // Interface style state
    const [interfaceStyle, setInterfaceStyle] = React.useState(() => {
        // Initialize from user preference or localStorage, default to 'light'
        return localStorage.getItem("interfaceStyle") || "light";
    });

    const { call } = useBluelagoon({
        method: "user",
    });

    const { call: setToken } = useBluelagoon({
        method: "setToken",
    });

    const { call: removeToken } = useBluelagoon({
        method: "removeToken",
    });

    const { call: updateUser, isLoading: isLoadingUpdateUser } = useBluelagoon({
        method: "updateUser",
    });

    const { call: logoutApi } = useBluelagoon({
        method: "logout",
    });

    const {
        call: getCompanyAgentUICredit,
        isLoading: isLoadingGetCompanyAgentUICredit,
    } = useBluelagoon({
        method: "getCompanyAgentUICredit",
        setIsLoading: false,
    });

    // Login mutation for token-based authentication
    const { call: login } = useBluelagoon({
        method: "login",
    });

    // Calculate trial end date and status
    const calculateTrialStatus = React.useCallback(() => {
        if (!user?.company?.trialEndDate) return null;

        const trialEndDate = new Date(user.company.trialEndDate);
        const now = new Date();

        return {
            endDate: trialEndDate,
            daysLeft: Math.max(
                0,
                Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24))
            ),
            isActive: now < trialEndDate,
        };
    }, [user?.company?.trialEndDate]);

    const trialStatus = calculateTrialStatus();

    // Filter out support subscriptions — they are managed separately
    const planSubscriptions = React.useMemo(() =>
        (user?.company?.stripeCustomer?.subscriptions || []).filter(
            (sub) => !isSupportPlan(sub.priceId)
        ),
        [user?.company?.stripeCustomer?.subscriptions]
    );
    const activePlanSubscription = React.useMemo(
        () =>
            planSubscriptions.find(
                (subscription) => subscription.status === "ACTIVE"
            ) || null,
        [planSubscriptions]
    );
    const subscriptionStatusPriority = React.useMemo(
        () => ({
            ACTIVE: 1,
            TRIALING: 2,
            PAST_DUE: 3,
            INCOMPLETE: 4,
            INCOMPLETE_EXPIRED: 5,
            UNPAID: 6,
            CANCELED: 7,
        }),
        []
    );
    const currentPlanSubscription = React.useMemo(() => {
        if (planSubscriptions.length === 0) return null;
        return planSubscriptions.reduce((prev, current) => {
            const prevPriority = subscriptionStatusPriority[prev.status] || 999;
            const currentPriority =
                subscriptionStatusPriority[current.status] || 999;
            return currentPriority < prevPriority ? current : prev;
        });
    }, [planSubscriptions, subscriptionStatusPriority]);

    // Check if user has any active subscription (not canceled, expired, or unpaid)
    const hasActiveSubscription = planSubscriptions.some(
        (subscription) =>
            subscription.status === "ACTIVE" ||
            subscription.status === "TRIALING" ||
            subscription.status === "PAST_DUE" ||
            subscription.status === "INCOMPLETE" ||
            subscription.status === "PAUSED"
    );

    // User is in trial only if:
    // 1. They have a subscription with status "TRIALING", OR
    // 2. They don't have any active subscription AND the trial period hasn't ended
    const isInTrial =
        planSubscriptions.some(
            (subscription) => subscription.status === "TRIALING"
        ) ||
        (!hasActiveSubscription && trialStatus?.isActive);

    // Check if trial has been canceled (subscription has CANCELED status)
    const isTrialCanceled =
        planSubscriptions.some(
            (subscription) => subscription.status === "CANCELED"
        ) ?? false;

    // Check if trial has expired (trial end date has passed AND no active subscription)
    const isTrialExpired =
        !hasActiveSubscription && trialStatus !== null && !trialStatus.isActive;

    // Combined check: trial has ended (either expired or canceled)
    const hasTrialEnded = isTrialExpired || isTrialCanceled;

    // Compute subscription alert status for global banner
    const subscriptionAlert = React.useMemo(() => {
        const hasActive = planSubscriptions.some(
            (s) => s.status === "ACTIVE" || s.status === "TRIALING"
        );

        if (hasActive) {
            const active = planSubscriptions.find(
                (s) => s.status === "ACTIVE" && s.cancelAtPeriodEnd
            );
            if (active?.currentPeriodEnd) {
                const daysLeft =
                    (new Date(active.currentPeriodEnd) - new Date()) /
                    (1000 * 60 * 60 * 24);
                if (daysLeft <= 30) {
                    return { status: "ENDING_SOON", subscription: active };
                }
            }
            return null;
        }

        const problemStatuses = [
            "PAST_DUE", "UNPAID", "INCOMPLETE",
            "INCOMPLETE_EXPIRED", "PAUSED", "CANCELED",
        ];
        const problem = planSubscriptions.find((s) => problemStatuses.includes(s.status));
        if (problem) return { status: problem.status, subscription: problem };

        return null;
    }, [planSubscriptions]);

    const TRIAL_BLOCKED_FEATURES = ["exportApp"];

    const handleIsFeatureEnabled = React.useCallback(
        (feature) => {
            const planEnabled = isFeatureEnabled(
                activePlanSubscription?.priceId,
                feature,
                user?.company?.id
            );
            if (planEnabled) return true;
            if (isInTrial && !TRIAL_BLOCKED_FEATURES.includes(feature)) return true;
            return false;
        },
        [activePlanSubscription?.priceId, user?.company?.id, isInTrial]
    );

    const numberOfUsers = React.useMemo(() => {
        return getNumberOfUsers(
            activePlanSubscription?.priceId
        );
    }, [activePlanSubscription?.priceId]);

    const getCustomPlanNumberFeature = React.useCallback(
        (feature) => {
            return getCustomPlanNumber(
                activePlanSubscription?.priceId,
                feature
            );
        },
        [activePlanSubscription?.priceId]
    );

    const currentUserCount = user?.company?._count?.users || 0;
    const userLimitReached = currentUserCount >= numberOfUsers;

    const hasCompletedTutorial =
        user?.tutorials?.welcome?.completed ||
        localStorage.getItem("hasCompletedTutorial") === "true";

    const handleGetUser = React.useCallback(() => {
        return new Promise((resolve, reject) => {
            setIsLoadingUser(true);
            call()
                .then((response) => {
                    setUser(response?.user);
                    if (response?.user?.email) {
                        setUserId(response?.user?.email);
                    }
                    if (response.user?.language) {
                        i18n.changeLanguage(response.user?.language);
                        localStorage.setItem(
                            "i18nextLng",
                            response.user?.language
                        );
                    }

                    // Cache company logo in localStorage
                    const companyId = response?.user?.company?.id;
                    if (companyId) {
                        const logoUrl = response?.user?.company?.logo?.url;
                        const key = `ws_logo_${companyId}`;
                        if (logoUrl) localStorage.setItem(key, logoUrl);
                        else localStorage.removeItem(key);
                    }

                    // Sync interface style from user preference if available
                    if (response?.user?.interfaceStyle) {
                        setInterfaceStyle(response.user.interfaceStyle);
                        localStorage.setItem(
                            "interfaceStyle",
                            response.user.interfaceStyle
                        );
                    }

                    setIsLoadingUser(false);
                    resolve(response?.user);
                })
                .catch((error) => {
                    setUser(null);
                    setIsLoadingUser(false);
                    reject(error);
                });
        });
    }, []);

    const handleUpdateUser = React.useCallback((params) => {
        return new Promise((resolve, reject) => {
            updateUser(params)
                .then((response) => {
                    handleGetUser();
                    resolve(response?.user);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }, []);

    const handleToggleInterfaceStyle = React.useCallback(async () => {
        const newStyle = interfaceStyle === "simple" ? "expert" : "simple";
        setInterfaceStyle(newStyle);
        localStorage.setItem("interfaceStyle", newStyle);

        try {
            // Update user preference in database
            await handleUpdateUser({ interfaceStyle: newStyle });
        } catch (error) {
            console.error("Failed to save interface style preference:", error);
            // Revert on error
            setInterfaceStyle(interfaceStyle);
            localStorage.setItem("interfaceStyle", interfaceStyle);
        }
    }, [interfaceStyle, handleUpdateUser]);

    // Handle token-based authentication
    const handleTokenAuth = React.useCallback(
        async (token) => {
            if (!token) return;

            try {
                // Set the token first
                setToken(token);

                // Attempt to get user data with the token
                await handleGetUser();

                // Close the auth modal if it was left open across the OAuth
                // redirect. sessionStorage persists "auth_modal_open" across
                // the hard navigation to the provider, so without this the
                // modal stays visible after a successful Google/Microsoft/etc.
                // callback even though the user is authenticated.
                handleSetShowAuthModal(false);

                // Clear the token from URL params after successful authentication
                const url = new URL(window.location);
                url.searchParams.delete("token");
                window.history.replaceState({}, "", url);
            } catch (error) {
                console.error("Token authentication failed:", error);
                // Remove invalid token
                removeToken();
            }
        },
        [setToken, handleGetUser, removeToken, handleSetShowAuthModal]
    );

    const handleCompleteTutorial = React.useCallback(
        async (selectedExample) => {
            try {
                // Gather all tutorial data from localStorage
                const userRole = localStorage.getItem("userRole");
                const userTools = localStorage.getItem("userTools");
                const userGoals = localStorage.getItem("userGoals");

                // Parse stored data
                const preferredTools = userTools ? JSON.parse(userTools) : [];
                const goalsData = userGoals ? JSON.parse(userGoals) : {};

                // Prepare automation goals and unsure status
                const automationGoals = goalsData.selectedGoals || [];
                const isUnsureAboutGoals = goalsData.isUnsure || false;

                // Create comprehensive onboarding metadata
                const onboardingMetadata = {
                    completedAt: new Date().toISOString(),
                    tutorialVersion: "1.0",
                    selectedExample: selectedExample,
                    completionSource: "tutorial-welcome",
                    hasCompletedOnboarding: true,
                };

                // Update user with all tutorial data
                const updateParams = {
                    onboardingMetadata,
                };

                // Only update fields that aren't already set or need updating
                if (userRole && userRole !== user?.role) {
                    updateParams.role = userRole;
                }
                if (preferredTools.length > 0) {
                    updateParams.preferredTools = preferredTools;
                }
                if (automationGoals.length > 0) {
                    updateParams.automationGoals = automationGoals;
                }
                if (isUnsureAboutGoals !== undefined) {
                    updateParams.isUnsureAboutGoals = isUnsureAboutGoals;
                }

                // Update user profile with all tutorial data
                await handleUpdateUser(updateParams);

                // Mark as completed in localStorage
                localStorage.setItem("hasCompletedTutorial", "true");

                return { success: true };
            } catch (error) {
                console.error("Failed to complete tutorial:", error);
                throw error;
            }
        },
        [user, handleUpdateUser]
    );

    const handleLogout = React.useCallback(
        async (logoutAll = false) => {
            try {
                // Invalidate session on the server first
                await logoutApi({ all: logoutAll });
            } catch (error) {
                // Log error but continue with local logout
                console.error("Server logout failed:", error);
            } finally {
                // Always clear local token and user state
                removeToken();
                setUser(null);
            }
        },
        [logoutApi, removeToken]
    );

    const handleAuthSuccess = React.useCallback(
        (user) => {
            handleSetShowAuthModal(false);
        },
        [handleSetShowAuthModal]
    );

    const handleGetCompanyAgentUICredit = React.useCallback(() => {
        getCompanyAgentUICredit().then((response) => {
            setCompanyAgentUICredit(response?.data?.credits);
        });
    }, [getCompanyAgentUICredit]);

    React.useEffect(() => {
        if (didLogin.current) return;

        // Check for token in URL params first
        const token = searchParams.get("token");
        if (token && token !== "undefined" && !user) {
            didLogin.current = true;
            handleTokenAuth(token);
        } else if (!user) {
            handleGetUser();
        }
    }, [searchParams]);

    React.useEffect(() => {
        if (!user?.id) return;

        // Initial fetch if not done
        if (!didGetCompanyAgentUICredit.current) {
            handleGetCompanyAgentUICredit();
            didGetCompanyAgentUICredit.current = true;
        }
    }, [user, handleGetCompanyAgentUICredit]);

    // Sync interface style when user changes
    React.useEffect(() => {
        if (user?.interfaceStyle && user.interfaceStyle !== interfaceStyle) {
            setInterfaceStyle(user.interfaceStyle);
            localStorage.setItem("interfaceStyle", user.interfaceStyle);
        }
    }, [user?.interfaceStyle, interfaceStyle]);

    return {
        user,
        setToken,
        getUser: handleGetUser,
        isLoadingUser,
        isAdmin,
        isSuperAdmin,
        isSupport,
        logout: handleLogout,
        trialStatus,
        isInTrial,
        isTrialExpired,
        isTrialCanceled,
        hasTrialEnded,
        updateUser: handleUpdateUser,
        isLoadingUpdateUser,
        completeTutorial: handleCompleteTutorial,
        hasCompletedTutorial,
        isClient,
        isComplianceOfficer,
        canModifyObfuscation,
        isFeatureEnabled: handleIsFeatureEnabled,
        interfaceStyle,
        toggleInterfaceStyle: handleToggleInterfaceStyle,
        showAuthModal,
        setShowAuthModal: handleSetShowAuthModal,
        handleAuthSuccess,
        numberOfUsers,
        currentUserCount,
        userLimitReached,
        isLoadingGetCompanyAgentUICredit,
        companyAgentUICredit,
        getCustomPlanNumberFeature,
        handleGetCompanyAgentUICredit,
        hasActiveSubscription,
        subscriptionAlert,
        planSubscriptions,
        activePlanSubscription,
        currentPlanSubscription,
    };
}

export function ProvideAuth({ children, toggleDarkMode }) {
    const auth = useProvideAuth({ toggleDarkMode });

    return (
        <authContext.Provider value={auth}>
            {children}
            <AuthModal
                open={auth.showAuthModal}
                onOpenChange={auth.setShowAuthModal}
                onAuthSuccess={auth.handleAuthSuccess}
            />
        </authContext.Provider>
    );
}

export function useAuth() {
    return useContext(authContext);
}

export function RequireAuth({ children, type }) {
    let auth = useAuth();
    let location = useLocation();

    // Wait for user loading to complete before making auth decisions
    if (auth.isLoadingUser) {
        return null;
    }

    if (!auth.user) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    if (type) {
        // check if one of the users role match one of the types
        const userRoles = auth.user?.roles || [];
        const roleNames = userRoles
            .map((role) => (typeof role === "string" ? role : role?.role?.name))
            .filter(Boolean);

        if (!roleNames.some((role) => type.includes(role))) {
            return <div>You don't have permission to access this page</div>;
        }
    }

    return children;
}
